/**
 * "Undo snap" — the way back from a shape the Smart Pen got wrong (SRS C5).
 *
 * It appears beside the shape the moment one snaps and stands for four seconds. That
 * is the whole safety net under the Smart Pen: a teacher mid-sentence who sees their
 * writing turn into a triangle needs one obvious thing to press, not a trip to the
 * undo button and a guess at how many presses it will take.
 *
 * A real button rather than something painted on the canvas, so it can be tabbed to,
 * pressed with a finger, and read out by a screen reader like anything else.
 */

import { canvasToScreen, type BoardObject, type Viewport } from '@opal/shared';

import { objectBounds } from '../../board/object-board.js';
import { useBoardStore } from '../../board/store.js';

/** How far under the shape the pill sits, in screen pixels. */
const GAP_PX = 12;

/** How much room to keep between the pill and the edge of the board. */
const MARGIN_PX = 56;

/**
 * Where the pill sits: under the middle of the shape, and always on screen.
 *
 * A shape snapped at the very bottom of the board would otherwise put its way back
 * off the edge, which is the one moment it is needed most.
 */
function placeUnder(object: BoardObject, viewport: Viewport): { left: number; top: number } {
  const bounds = objectBounds(object);
  const under = canvasToScreen(viewport, {
    x: bounds.x + bounds.width / 2,
    y: bounds.y + bounds.height,
  });
  return {
    left: Math.min(Math.max(under.x, MARGIN_PX), Math.max(MARGIN_PX, viewport.widthPx - MARGIN_PX)),
    top: Math.min(under.y + GAP_PX, Math.max(0, viewport.heightPx - MARGIN_PX)),
  };
}

export function UndoSnapPill() {
  const snapUndo = useBoardStore((state) => state.snapUndo);
  const viewport = useBoardStore((state) => state.viewport);
  if (!snapUndo) return null;

  const at = placeUnder(snapUndo.object, viewport);

  return (
    <button
      type="button"
      data-undo-snap
      // The board must treat a press here as a press, not as the start of a line.
      data-board-ui
      onClick={snapUndo.undo}
      // Pressing it is not also a tap on the board behind it.
      onPointerDown={(event) => {
        event.stopPropagation();
      }}
      className="absolute h-touch -translate-x-1/2 rounded-full bg-surface px-4 text-sm font-medium text-text-primary shadow-md ring-1 ring-border transition duration-fast ease-out hover:bg-surface-hover"
      style={{ left: at.left, top: at.top, zIndex: 50 }}
    >
      Undo snap
    </button>
  );
}
